// Hand-rolled types covering only what the admin TS modules touch.
// `npm run supabase:db:types` will overwrite `types.gen.ts` with the full
// Database schema once the local stack is running. We re-export those when
// available; until then this file is the canonical surface.

export type Role = 'super_admin' | 'project_admin' | 'source_person' | 'viewer';
export type UserStatus = 'active' | 'inactive' | 'invited';

export type ValidationStatus = 'unverified' | 'field_confirmed' | 'corrected' | 'rejected';

export interface UserProfile {
  user_id: string;
  email: string;
  full_name: string | null;
  role: Role;
  status: UserStatus;
  organization: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  last_sign_in_at?: string | null;
  banned_until?: string | null;
}

export interface AdminInvite {
  id: string;
  email: string;
  role: Role;
  invite_status: 'pending' | 'accepted' | 'revoked';
  invited_by: string | null;
  organization: string | null;
  notes: string | null;
  created_at: string;
  accepted_at: string | null;
}

export interface RawAssetData {
  id: string;
  source_name: string | null;
  source_file: string | null;
  row_hash: string | null;
  asset_id: string | null;
  year: number | null;
  province_code: string | null;
  district_code: string | null;
  asset_type: string | null;
  raw_payload: Record<string, unknown>;
  validation_status: ValidationStatus;
  validation_notes: string | null;
  corrected_payload: Record<string, unknown> | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type IndicatorLifecycle = 'draft' | 'validated' | 'active' | 'deprecated';
export type IndicatorBlock = 'CAUSE' | 'IMPACT' | 'BRIDGE';
export type IndicatorOrientation = 'positive' | 'negative' | 'neutral';

export interface Indicator {
  indicator_id: string;
  block: string | null;
  dimension: string | null;
  name_id: string | null;
  name_en: string | null;
  observation_field: string | null;
  orientation: string | null;
  unit: string | null;
  normalization: string | null;
  mapping: Record<string, unknown> | null;
  // 0006 columns
  block_extended: IndicatorBlock | null;
  timestamp_required: string | null;
  data_status: string | null;
  visibility: string | null;
  // 0007 columns
  formula: string | null;
  operational_definition: string | null;
  threshold_low: number | null;
  threshold_high: number | null;
  required_evidence: string | null;
  collection_frequency: string | null;
  lifecycle_status: IndicatorLifecycle;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type AssignmentStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'needs_revision';
export type AssignmentPriority = 'low' | 'normal' | 'high' | 'urgent';
export type AssignmentSourceType = 'interview' | 'fgd' | 'field_observation' | 'document' | 'bwm_input' | 'raw_asset_data' | 'other';

export interface Assignment {
  id: string;
  assignee_user_id: string;
  source_type: AssignmentSourceType;
  source_id: string | null;
  title: string;
  description: string | null;
  due_at: string | null;
  status: AssignmentStatus;
  priority: AssignmentPriority;
  assigned_by: string | null;
  assigned_at: string;
  completed_at: string | null;
  reviewer_notes: string | null;
  assignee_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DataChangeAuditRow {
  id: string;
  ts: string;
  table_name: string;
  record_id: string;
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  before_payload: unknown;
  after_payload: unknown;
  changed_by: string | null;
  prev_hash: string | null;
  curr_hash: string | null;
}

export interface StoreSpec {
  keyPath: string;
  remoteTable?: string;
}

export const STORES: Record<string, StoreSpec> = {
  assets: { keyPath: 'asset_id' },
  yearly_observations: { keyPath: 'observation_id' },
  interviews: { keyPath: 'interview_id' },
  fgds: { keyPath: 'fgd_id' },
  field_observations: { keyPath: 'observation_id' },
  documents: { keyPath: 'document_id' },
  bwm_experts: { keyPath: 'expert_id' },
  bwm_inputs: { keyPath: 'input_id' },
  bwm_weights: { keyPath: 'weight_id' },
  indicators: { keyPath: 'indicator_id' },
  scores: { keyPath: 'score_id' },
  audit_logs: { keyPath: 'audit_id' },
  settings: { keyPath: 'key' },
  provinces: { keyPath: 'province_code' },
  districts: { keyPath: 'district_code' },
  recommendations: { keyPath: 'recommendation_id' },
  raw_asset_data: { keyPath: 'id' },
  admin_invites: { keyPath: 'id' },
  data_change_audit: { keyPath: 'id' },
};
